// @ts-strict-ignore
import React, { useEffect, useState, useMemo } from 'react';
import { MultiSelect, Text } from '@usealloy/component-library';

import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import { getDataTypeOptions, getDimensionOptions } from 'flow/service/actions';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import type { ActionValueOption } from './types';
import {
  Option,
  SingleValue,
} from './dropdownComponents';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { setLinkedChildActionCreateActionPayload } from '~/dashboard/views/workflows/store/redux/slice';
import { generatePublicId } from 'flow/service/publicIds';
import { selectMatrixModelActions } from '~/dashboard/views/workflows/store/redux/selectors';

export default function PreviewMatrixModelDropdowns() {
  const dispatch = useAppDispatch();
  const [dataTypeOption, setDataTypeOption] = useState<ActionValueOption>(null);
  const [dimensionsOption, setDimensionsOption] = useState<ActionValueOption>(null);
  const [nameOption, setNameOption] = useState<ActionValueOption>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const matrixModelActions = useAppSelector(selectMatrixModelActions);

  const existingMatrixNames = useMemo(() => new Set(
    matrixModelActions
      .map(action => action.matrixModel?.name)
      .filter((name): name is string => !!name),
  ), [matrixModelActions]);

  const validateMatrixName = (name: string) => {
    if (existingMatrixNames.has(name)) {
      setNameError('A matrix with this name already exists in this workflow version.');
    } else {
      setNameError(null);
    }
  };

  const actionValueMultiSelectRef = React.useRef(null);

  const formatSelectedDimensions = (
    selectedOption: ActionValueOption,
  ): ActionValueOption => {
    switch (selectedOption.value) {
      case '1':
        return {
          label: '1 Dimension,', value: '1',
        };
      case '2':
        return {
          label: '2 Dimensions,', value: '2',
        };
      default:
        return null;
    }
  };

  const formatSelectedDataType = (
    selectedOption: ActionValueOption,
  ): ActionValueOption => ({
    ...selectedOption,
    label: `${selectedOption.label},`,
  });

  useEffect(() => {
    if (dataTypeOption && dimensionsOption && nameOption && !nameError) {
      dispatch(setLinkedChildActionCreateActionPayload({
        type: ActionType.MATRIX_MODEL,
        args: {
          name: nameOption.value,
          dimensions: dimensionsOption.value === '1' ? 1 : 2,
          dataType: dataTypeOption.value,
          publicId: generatePublicId('action', ActionType.MATRIX_MODEL),
        },
      }));
    }
  }, [dispatch, dataTypeOption, dimensionsOption, nameOption, nameError]);

  return (
    <>
      <MultiSelect
        {...MULTISELECT_PROPS}
        // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
        customStyleFunctions={{
          ...MULTISELECT_PROPS.customStyleFunctions,
          menu: styles => ({
            ...styles,
            minWidth: '320px',
          }),
        }}
        // @ts-expect-error - TODO: fix this
        autoFocus
        openMenuOnFocus
        closeMenuOnSelect
        isMulti={false}
        onChange={(
          value: ActionValueOption,
        ) => setDataTypeOption(formatSelectedDataType(value))}
        options={getDataTypeOptions()}
        value={dataTypeOption}
        placeholder="select Matrix type"
        components={{
          Option: Option<ActionValueOption>,
          SingleValue,
        }}
        ref={actionValueMultiSelectRef}
      />
      {dataTypeOption && (
        <MultiSelect
          {...MULTISELECT_PROPS}
          // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
          customStyleFunctions={{
            ...MULTISELECT_PROPS.customStyleFunctions,
            menu: styles => ({
              ...styles,
              minWidth: '320px',
            }),
          }}
          // @ts-expect-error - the Option type is mis-mapped in the underlying library
          autoFocus
          openMenuOnFocus
          closeMenuOnSelect
          isMulti={false}
          onChange={(
            value: ActionValueOption,
          ) => setDimensionsOption(formatSelectedDimensions(value))}
          options={getDimensionOptions()}
          value={dimensionsOption}
          placeholder="select number of dimensions"
          components={{
            Option: Option<ActionValueOption>,
            SingleValue,
          }}
          ref={actionValueMultiSelectRef}
        />
      )}
      {dimensionsOption && (
        <MultiSelect
          {...MULTISELECT_PROPS}
          // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
          customStyleFunctions={{
            ...MULTISELECT_PROPS.customStyleFunctions,
            menu: styles => ({
              ...styles,
              minWidth: '320px',
            }),
          }}
          // @ts-expect-error - the Option type is mis-mapped in the underlying library
          autoFocus
          openMenuOnFocus
          closeMenuOnSelect
          isCreatable
          onCreateOption={value => {
            validateMatrixName(value.value);
            setNameOption({
              ...value,
              meta: {
                textColor: 'var(--cl-juicyBlue-500)',
              },
            });
          }}
          formatCreateLabel={newValue => (`Matrix Name: ${newValue}`)}
          noOptionsMessage={() => null}
          isMulti={false}
          value={nameOption}
          placeholder="enter Matrix Name"
          components={{
            Option: Option<ActionValueOption>,
            SingleValue,
          }}
          ref={actionValueMultiSelectRef}
        />
      )}
      {nameError && (
        <Text style={{ color: 'var(--cl-red-500)' }} role="alert">
          {nameError}
        </Text>
      )}
    </>
  );
}
